import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { GetWorkstreams } from '../../redux/dataroomSlice';
import { GetAllGroups, GetProjectUsers, InviteTeamPeople, PatchDataroomGroup, PostDataroomGroup, ResendInvite } from '../../redux/teamSlice';
import { getInitials } from '../../utils/commonUtils';
import { featureSummary, isAdminGroup, presetFromPermissions, storedFeatureKeys, vdrPresetTitle, VDR_PRESETS } from '../NewProjectFlow/accessGroupCatalog';
import AccessGroupForm from './AccessGroupForm';
import AddMembersDrawer from './AddMembersDrawer';
import AdvancedVdrPermissions from './AdvancedVdrPermissions';
import MemberDetailsModal from './MemberDetailsModal';
import { buildPeople } from './peopleModel';
import styles from './css/index.module.css';

function workstreamLabel(group, workstreams) {
    if (group?.all_workstreams) return 'All workstreams';
    const aclSet = new Set((group?.acl || []).map(id => String(id)));
    const names = (workstreams || []).filter(ws => aclSet.has(String(ws.acl))).map(ws => ws.name);
    return names.length ? names.join(' · ') : 'None selected';
}

function extraAccess(group) {
    const items = [];
    if (group?.permissions?.intelligence_insights) items.push('Intelligence Insights');
    if (group?.permissions?.project_mail) items.push('Project Mail');
    if (group?.permissions?.project_meetings) items.push('Meeting Recordings');
    return items;
}

function memberLabel(count, pending) {
    const members = `${count} member${count === 1 ? '' : 's'}`;
    if (!pending) return members;
    return `${members} · ${pending} pending`;
}

export default function ProjectTeamAccess() {
    const dispatch = useDispatch();
    const selectedProject = useSelector(state => state.projectData.selectedProject);
    const groupState = useSelector(state => state.team.groups);
    const groups = Array.isArray(groupState) ? groupState : [];
    const projectUsersAll = useSelector(state => state.team.projectUsersAll) || [];
    const currentUser = useSelector(state => state.user.user);
    const [tab, setTab] = React.useState('people');
    const [screen, setScreen] = React.useState({ type: 'list' });
    const [workstreams, setWorkstreams] = React.useState([]);
    const [adding, setAdding] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [selectedPerson, setSelectedPerson] = React.useState(null);
    const [query, setQuery] = React.useState('');
    const [groupFilter, setGroupFilter] = React.useState('all');
    const [statusFilter, setStatusFilter] = React.useState('all');
    const [sort, setSort] = React.useState({ key: 'name', dir: 'asc' });

    React.useEffect(() => {
        dispatch(GetProjectUsers());
        dispatch(GetAllGroups());
        dispatch(GetWorkstreams()).then(result => {
            const list = result?.payload?.data;
            if (Array.isArray(list)) setWorkstreams(list.map(ws => ({ id: ws.id, name: ws.name, acl: ws.acl })));
        });
    }, [dispatch, selectedProject?.id]);

    const accessGroups = groups.filter(group => group?.id && !isAdminGroup(group));
    const people = React.useMemo(() => {
        const vdrAdminId = selectedProject?.vdr?.vdr_admin ? String(selectedProject.vdr.vdr_admin) : '';
        const listedAdmins = new Set((selectedProject?.project_admins || []).map(admin => String(admin.user_id || admin.id || '')));
        return buildPeople(projectUsersAll).map(person => {
            const isOwner = vdrAdminId && String(person.userId) === vdrAdminId;
            const listed = person.userId && listedAdmins.has(String(person.userId));
            if (!isOwner && !listed) return person;
            return {
                ...person,
                isAdmin: true,
                accessGroupName: person.accessGroupName || 'Project Administrator',
            };
        });
    }, [projectUsersAll, selectedProject]);
    const counts = React.useMemo(() => {
        const byGroup = {};
        people.forEach(person => {
            if (!person.accessGroupId) return;
            const bucket = byGroup[person.accessGroupId] || { active: [], pending: 0 };
            if (person.status === 'pending') bucket.pending += 1;
            else bucket.active.push(person);
            byGroup[person.accessGroupId] = bucket;
        });
        return byGroup;
    }, [people]);

    const admins = people.filter(person => person.isAdmin).sort((a, b) => {
        if (String(a.userId) === String(currentUser?.id)) return -1;
        if (String(b.userId) === String(currentUser?.id)) return 1;
        return a.fullName.localeCompare(b.fullName);
    });

    const members = people
        .filter(person => !person.isAdmin)
        .filter(person => {
            const haystack = `${person.fullName} ${person.email}`.toLowerCase();
            if (query && !haystack.includes(query.trim().toLowerCase())) return false;
            if (groupFilter !== 'all' && String(person.accessGroupId) !== String(groupFilter)) return false;
            if (statusFilter === 'active' && person.status !== 'active') return false;
            if (statusFilter === 'pending' && person.status !== 'pending') return false;
            return true;
        })
        .sort((a, b) => {
            const dir = sort.dir === 'asc' ? 1 : -1;
            const left = sort.key === 'group' ? a.accessGroupName : sort.key === 'status' ? a.status : a.fullName;
            const right = sort.key === 'group' ? b.accessGroupName : sort.key === 'status' ? b.status : b.fullName;
            return String(left).localeCompare(String(right)) * dir;
        });

    const refresh = () => {
        dispatch(GetProjectUsers());
        dispatch(GetAllGroups());
    };

    const saveGroup = async draft => {
        const preset = VDR_PRESETS.find(item => item.key === draft.vdrPreset);
        const body = {
            name: draft.name,
            vdr: selectedProject?.vdr?.id,
            permissions: {
                ...(preset?.permissions || {}),
                intelligence_insights: Boolean(draft.intelligence),
                project_mail: Boolean(draft.projectMail),
                project_meetings: Boolean(draft.projectMeetings),
            },
            acls: draft.acls,
            feature_keys: draft.features,
            all_workstreams: draft.workstreamMode === 'all',
        };
        setSaving(true);
        const result =
            screen.type === 'edit'
                ? await dispatch(PatchDataroomGroup({ ...body, id: screen.group.id }))
                : await dispatch(PostDataroomGroup({ ...body, project: selectedProject?.id }));
        setSaving(false);
        if (String(result?.type || '').endsWith('/fulfilled')) {
            refresh();
            setScreen({ type: 'list' });
            setTab('groups');
        }
    };

    const sendInvites = async draftPeople => {
        setSending(true);
        const adminGroup = groups.find(group => isAdminGroup(group));
        const people = draftPeople.map(person => ({
            email: person.email.trim().toLowerCase(),
            group_type: person.admin ? adminGroup?.id : person.groupId,
            project_administrator: Boolean(person.admin),
        }));
        if (people.some(person => person.project_administrator && !person.group_type)) {
            setSending(false);
            toast.error('This project has no Administrator group.');
            return;
        }
        const result = await dispatch(
            InviteTeamPeople({
                vdr: selectedProject?.vdr?.id,
                people,
            }),
        );
        setSending(false);
        if (result?.meta?.requestStatus === 'rejected') {
            const payload = result.payload;
            const message = payload?.message || payload?.detail || payload?.people?.[0] || 'Some invitations could not be sent.';
            toast.error(typeof message === 'string' ? message : 'Some invitations could not be sent.');
            if (payload?.status === 207) {
                setAdding(false);
                refresh();
            }
            return;
        }
        const body = result?.payload?.data || {};
        const failures = Object.entries(body).filter(([, value]) => typeof value === 'string');
        const adminFailures = Object.entries(body).filter(([, value]) => value && typeof value === 'object' && value.administrator_error);
        if (failures.length) {
            failures.forEach(([email, message]) => toast.error(`${email}: ${message}`));
        }
        adminFailures.forEach(([email, value]) => toast.error(`${email} was invited, but administrator access failed: ${value.administrator_error}`));
        if (!failures.length) toast.success(adminFailures.length ? 'Invitations sent with a warning' : 'Invitations sent');
        setAdding(false);
        refresh();
    };

    const resend = async person => {
        const inviteIds = [...new Set((person.inviteIds || []).filter(Boolean))];
        if (!inviteIds.length) {
            toast.error('No pending invitation to resend.');
            return;
        }
        for (const inviteId of inviteIds) {
            const result = await dispatch(
                ResendInvite({
                    id: inviteId,
                    email: [person.email],
                    vdr: selectedProject?.vdr?.id,
                    project: localStorage.getItem('project_id'),
                    vdrinvitation_permission: { invitation: inviteId },
                }),
            );
            if (!String(result?.type || '').endsWith('/fulfilled')) return;
        }
    };

    const toggleSort = key => {
        setSort(prev => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    };

    if (screen.type === 'vdr') {
        return (
            <div className={styles.page}>
                <AdvancedVdrPermissions group={screen.group} onBack={() => setScreen({ type: 'view', group: screen.group })} />
            </div>
        );
    }

    if (screen.type === 'create' || screen.type === 'edit' || screen.type === 'view') {
        const group = screen.group;
        const bucket = group ? counts[String(group.id)] : null;
        const headCount = (bucket?.active.length || 0) + (bucket?.pending || 0);
        return (
            <div className={styles.page}>
                <AccessGroupForm
                    mode={screen.type}
                    group={group}
                    workstreams={workstreams}
                    existingNames={accessGroups.filter(item => item.id !== group?.id).map(item => item.name)}
                    memberCount={group ? `${headCount} member${headCount === 1 ? '' : 's'}` : null}
                    saving={saving}
                    onCancel={() => setScreen({ type: 'list' })}
                    onSave={saveGroup}
                    onEdit={() => setScreen({ type: 'edit', group })}
                    onAdvanced={group ? () => setScreen({ type: 'vdr', group }) : undefined}
                />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h2 className={styles.title}>Team & Access</h2>
                    <p className={styles.subtitle}>Manage project members, access groups and invitations.</p>
                </div>
                {tab === 'people' ? (
                    <button type="button" className={styles.primaryBtn} onClick={() => setAdding(true)}>
                        + Add team members
                    </button>
                ) : (
                    <button type="button" className={styles.primaryBtn} onClick={() => setScreen({ type: 'create' })}>
                        + Create access group
                    </button>
                )}
            </div>
            <div className={styles.tabs}>
                <button type="button" className={`${styles.tab} ${tab === 'people' ? styles.tabActive : ''}`} onClick={() => setTab('people')}>
                    People
                </button>
                <button type="button" className={`${styles.tab} ${tab === 'groups' ? styles.tabActive : ''}`} onClick={() => setTab('groups')}>
                    Access Groups
                </button>
            </div>

            {tab === 'people' ? (
                <div>
                    <p className={styles.groupLabel}>Project Administrators</p>
                    <div className={styles.card}>
                        {admins.length === 0 ? <p className={styles.meta}>No project administrators yet.</p> : null}
                        {admins.map(person => (
                            <div key={person.email} className={styles.person}>
                                <button type="button" className={styles.rowBtn} onClick={() => setSelectedPerson(person)}>
                                    <span className={`${styles.avatar} ${styles.avatarLg}`}>{getInitials(person.fullName) || person.email.slice(0, 2).toUpperCase()}</span>
                                    <span className={styles.personMain}>
                                        <p className={styles.personName}>
                                            {person.fullName}
                                            {String(person.userId) === String(currentUser?.id) ? <span className={styles.you}> (You)</span> : null}
                                        </p>
                                        <p className={styles.personEmail}>{person.email}</p>
                                        <p className={styles.personGroup}>{person.accessGroupName}</p>
                                    </span>
                                </button>
                                <span className={styles.pills}>
                                    <span className={styles.pill}>Project Administrator</span>
                                    <span className={person.status === 'pending' ? styles.pillPending : styles.pillActive}>
                                        {person.status === 'pending' ? 'Invitation pending' : 'Active'}
                                    </span>
                                    {person.status === 'pending' ? (
                                        <button type="button" className={styles.textBtn} onClick={() => resend(person)}>
                                            Resend
                                        </button>
                                    ) : null}
                                    <button type="button" className={styles.textBtn} aria-label="Member details" onClick={() => setSelectedPerson(person)}>
                                        •••
                                    </button>
                                </span>
                            </div>
                        ))}
                    </div>

                    <p className={styles.groupLabel}>Team Members</p>
                    <div className={styles.toolbar}>
                        <input className={`${styles.input} ${styles.search}`} value={query} placeholder="Search by name…" onChange={event => setQuery(event.target.value)} />
                        <select className={`${styles.select} ${styles.filterSelect}`} value={groupFilter} onChange={event => setGroupFilter(event.target.value)}>
                            <option value="all">All access groups</option>
                            {accessGroups.map(group => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                        <select className={`${styles.select} ${styles.filterSelect}`} value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                            <option value="all">All statuses</option>
                            <option value="active">Active</option>
                            <option value="pending">Invitation pending</option>
                        </select>
                        <span className={styles.chips}>
                            <span className={styles.meta}>Sort:</span>
                            {[
                                ['name', 'Name'],
                                ['group', 'Group'],
                                ['status', 'Status'],
                            ].map(([key, label]) => (
                                <button key={key} type="button" className={`${styles.chip} ${sort.key === key ? styles.chipOn : ''}`} onClick={() => toggleSort(key)}>
                                    {label}
                                    {sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                                </button>
                            ))}
                        </span>
                    </div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Access Group</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.length === 0 ? (
                                    <tr>
                                        <td colSpan={4}>
                                            <p className={styles.empty}>No team members match.</p>
                                        </td>
                                    </tr>
                                ) : null}
                                {members.map(person => (
                                    <tr key={person.email}>
                                        <td>
                                            <button type="button" className={styles.rowBtn} onClick={() => setSelectedPerson(person)}>
                                                <span className={styles.avatar}>{getInitials(person.fullName) || person.email.slice(0, 1).toUpperCase()}</span>
                                                {person.fullName}
                                            </button>
                                        </td>
                                        <td>{person.email}</td>
                                        <td>{person.accessGroupName}</td>
                                        <td>
                                            <span className={styles.statusCell}>
                                                <span className={person.status === 'pending' ? styles.pillPending : styles.pillActive}>
                                                    {person.status === 'pending' ? 'Invitation pending' : 'Active'}
                                                </span>
                                                {person.status === 'pending' ? (
                                                    <button type="button" className={styles.textBtn} onClick={() => resend(person)}>
                                                        Resend
                                                    </button>
                                                ) : null}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className={styles.stack}>
                    {accessGroups.length === 0 ? <p className={styles.empty}>No access groups yet.</p> : null}
                    {accessGroups.map(group => {
                        const bucket = counts[String(group.id)] || { active: [], pending: 0 };
                        const features = storedFeatureKeys(group);
                        const extras = extraAccess(group);
                        return (
                            <article key={group.id} className={styles.card}>
                                <div className={styles.cardHead}>
                                    <div>
                                        <h3 className={styles.cardTitle}>{group.name}</h3>
                                        <p className={styles.meta}>{memberLabel(bucket.active.length, bucket.pending)}</p>
                                    </div>
                                    <div className={styles.actions}>
                                        <button type="button" className={styles.textBtn} onClick={() => setScreen({ type: 'view', group })}>
                                            View
                                        </button>
                                        <button type="button" className={styles.textBtn} onClick={() => setScreen({ type: 'edit', group })}>
                                            Edit
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.avatars}>
                                    {bucket.active.slice(0, 8).map(person => (
                                        <span key={person.email} className={styles.avatar}>
                                            {getInitials(person.fullName) || person.email.slice(0, 1).toUpperCase()}
                                        </span>
                                    ))}
                                </div>
                                <div className={styles.facts}>
                                    {features ? (
                                        <div>
                                            <p className={styles.factLabel}>Workspace</p>
                                            <p className={styles.factValue}>{featureSummary(features) || 'No features selected'}</p>
                                        </div>
                                    ) : null}
                                    <div>
                                        <p className={styles.factLabel}>Workstreams</p>
                                        <p className={styles.factValue}>{workstreamLabel(group, workstreams)}</p>
                                    </div>
                                    {extras.length ? (
                                        <div className={styles.factWide}>
                                            <p className={styles.factLabel}>Additional project access</p>
                                            <p className={`${styles.factValue} ${styles.green}`}>{extras.join(' · ')}</p>
                                        </div>
                                    ) : null}
                                    <div>
                                        <p className={styles.factLabel}>VDR</p>
                                        <p className={styles.factValue}>{vdrPresetTitle(presetFromPermissions(group.permissions))}</p>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {adding ? <AddMembersDrawer groups={accessGroups} saving={sending} onClose={() => setAdding(false)} onSend={sendInvites} /> : null}
            {selectedPerson ? <MemberDetailsModal person={selectedPerson} onClose={() => setSelectedPerson(null)} /> : null}
        </div>
    );
}
